// src/features/rewardSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type { AppDispatch, RootState } from "../app/store";
import { apiForSSO } from "../utils/utils";

export interface AssignedRewardBox {
  box: string;
  reward_id: string | null; // null when not assigned; otherwise reward id string
}

type AsyncStatus = "idle" | "loading" | "succeeded" | "failed";

interface RewardState {
  assignedRewardBoxes: AssignedRewardBox[];
  fetchAssignedStatus: AsyncStatus;
  fetchAssignedError: string | null;
  isFetchAssignedInProgress: boolean;

  // selection
  selectedRewardBox: AssignedRewardBox | null;

  // assign API
  assignRewardStatus: AsyncStatus;
  assignRewardError: string | null;
  isAssignRewardInProgress: boolean;
}

const initialState: RewardState = {
  assignedRewardBoxes: [],
  fetchAssignedStatus: "idle",
  fetchAssignedError: null,
  isFetchAssignedInProgress: false,

  selectedRewardBox: null,

  assignRewardStatus: "idle",
  assignRewardError: null,
  isAssignRewardInProgress: false,
};

export const fetchAssignedRewardBoxes = createAsyncThunk<
  AssignedRewardBox[],
  void,
  { dispatch: AppDispatch; rejectValue: string }
>("rewards/fetchAssignedRewardBoxes", async (_arg, { rejectWithValue }) => {
  try {
    const res = await apiForSSO.get("/admin/assigned_reward_boxes");
    return res.data as AssignedRewardBox[];
  } catch (error: any) {
    if (error?.response) {
      const status = error.response.status;
      const detail =
        error.response.data?.detail ||
        error.response.data?.message ||
        "Request failed";
      if (status === 400) return rejectWithValue(detail);
      if (status === 401) return rejectWithValue("Unauthorized. Please sign in again.");
      return rejectWithValue(`${status}: ${detail}`);
    }
    return rejectWithValue("Network error. Please try again.");
  }
});

/**
 * Assign a reward to a box.
 * Example:
 *   GET /admin/assign_rewards_box?box_name=Silver%20Box&reward_id=123
 * Success: { detail: "Operation is successful." }
 */
export const assignRewardBox = createAsyncThunk<
  { detail: string },
  { box_name: string; reward_id: string },
  { dispatch: AppDispatch; rejectValue: string }
>("rewards/assignRewardBox", async ({ box_name, reward_id }, { dispatch, rejectWithValue }) => {
  try {
    const res = await apiForSSO.get("/admin/assign_rewards_box", {
      params: { box_name, reward_id },
    });
    // Refresh list after successful assignment
    dispatch(fetchAssignedRewardBoxes());
    return res.data as { detail: string };
  } catch (error: any) {
    if (error?.response) {
      const status = error.response.status;
      const detail =
        error.response.data?.detail ||
        error.response.data?.message ||
        "Request failed";
      if (status === 400) return rejectWithValue(detail);
      if (status === 401) return rejectWithValue("Unauthorized. Please sign in again.");
      return rejectWithValue(`${status}: ${detail}`);
    }
    return rejectWithValue("Network error. Please try again.");
  }
});

const rewardSlice = createSlice({
  name: "rewards",
  initialState,
  reducers: {
    resetFetchAssignedStatus(state) {
      state.fetchAssignedStatus = "idle";
      state.fetchAssignedError = null;
      state.isFetchAssignedInProgress = false;
    },
    setAssignedRewardBoxes(state, action: PayloadAction<AssignedRewardBox[]>) {
      state.assignedRewardBoxes = action.payload;
    },
    clearAssignedRewardBoxes(state) {
      state.assignedRewardBoxes = [];
    },
    setSelectedRewardBox(state, action: PayloadAction<AssignedRewardBox | null>) {
      state.selectedRewardBox = action.payload;
    },
    resetAssignRewardStatus(state) {
      state.assignRewardStatus = "idle";
      state.assignRewardError = null;
      state.isAssignRewardInProgress = false;
    },
  },
  extraReducers: (builder) => {
    // fetchAssignedRewardBoxes
    builder
      .addCase(fetchAssignedRewardBoxes.pending, (state) => {
        state.fetchAssignedStatus = "loading";
        state.fetchAssignedError = null;
        state.isFetchAssignedInProgress = true;
      })
      .addCase(fetchAssignedRewardBoxes.fulfilled, (state, action: PayloadAction<AssignedRewardBox[]>) => {
        state.fetchAssignedStatus = "succeeded";
        state.assignedRewardBoxes = action.payload || [];
        state.isFetchAssignedInProgress = false;
      })
      .addCase(fetchAssignedRewardBoxes.rejected, (state, action) => {
        state.fetchAssignedStatus = "failed";
        state.fetchAssignedError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to fetch assigned reward boxes";
        state.isFetchAssignedInProgress = false;
      });

    // assignRewardBox
    builder
      .addCase(assignRewardBox.pending, (state) => {
        state.assignRewardStatus = "loading";
        state.assignRewardError = null;
        state.isAssignRewardInProgress = true;
      })
      .addCase(assignRewardBox.fulfilled, (state) => {
        state.assignRewardStatus = "succeeded";
        state.isAssignRewardInProgress = false;
      })
      .addCase(assignRewardBox.rejected, (state, action) => {
        state.assignRewardStatus = "failed";
        state.assignRewardError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to assign reward to box";
        state.isAssignRewardInProgress = false;
      });
  },
});

export const {
  resetFetchAssignedStatus,
  setAssignedRewardBoxes,
  clearAssignedRewardBoxes,
  setSelectedRewardBox,
  resetAssignRewardStatus,
} = rewardSlice.actions;

export default rewardSlice.reducer;
